export type AnalyticsConsentDecision = "granted" | "denied";

export const analyticsConsentStorageKey = "smartguide.analytics-marketing-consent.v1";
export const analyticsConsentChangedEventName = "smartguide:analytics-consent-changed";

export interface AnalyticsConsentChangedDetail {
    decision: AnalyticsConsentDecision;
}

type ConsentStorage = Pick<Storage, "getItem" | "setItem">;

export class AnalyticsConsentState {
    private currentDecision: AnalyticsConsentDecision | null | undefined;

    constructor(
        private readonly windowRef: Window,
        private readonly storageProvider: () => ConsentStorage = () => windowRef.localStorage
    ) {}

    getDecision(): AnalyticsConsentDecision | null {
        if (this.currentDecision !== undefined) return this.currentDecision;

        try {
            const storedDecision = this.storageProvider().getItem(analyticsConsentStorageKey);
            this.currentDecision = storedDecision === "granted" || storedDecision === "denied"
                ? storedDecision
                : null;
        } catch {
            this.currentDecision = null;
        }

        return this.currentDecision;
    }

    isGranted(): boolean {
        return this.getDecision() === "granted";
    }

    setDecision(decision: AnalyticsConsentDecision): void {
        this.currentDecision = decision;
        try {
            this.storageProvider().setItem(analyticsConsentStorageKey, decision);
        } catch {
            // The in-memory decision still applies to this document.
        }
        this.dispatchDecision(decision);
    }

    announceCurrentDecision(): void {
        const decision = this.getDecision();
        if (decision) this.dispatchDecision(decision);
    }

    private dispatchDecision(decision: AnalyticsConsentDecision): void {
        this.windowRef.dispatchEvent(new CustomEvent<AnalyticsConsentChangedDetail>(
            analyticsConsentChangedEventName,
            { detail: { decision } }
        ));
    }
}

export const browserAnalyticsConsentState = new AnalyticsConsentState(window);
